<template>
  <div
    class="modal fade"
    id="inquiryModal"
    tabindex="-1"
    aria-labelledby="inquiryModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg rounded-3">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title" id="inquiryModalLabel">
            Custom Cake Inquiry — {{ customCake.name }}
          </h5>
          <button
            type="button"
            class="btn-close btn-close-dark"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <form class="row gy-2 gx-3 align-items-center" @submit.prevent="submitInquiryForm">
            <div class="col-md-4">
              <label class="visually-hidden" for="autoSizingInput">Pickup Date & Time</label>
              <VueDatePicker
                v-model="inquiryForm.pickup_date_time"
                :enable-time-picker="true"
                input-class="form-control"
                placeholder="Select date & time"
              />
            </div>
            <div class="col-md-4">
              <select class="form-select" v-model="inquiryForm.store_id" required>
                <option value="">Select Store</option>  
                <option v-for="store in stores" :key="store.id" :value="store.id">
                  {{ store.name }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <select class="form-select" v-model="inquiryForm.delivery_option" required>
                <option value="pickup">Pickup</option>                
              </select>
            </div>        
            <div class="col-md-4">
              <select class="form-select" ref="categorySelectRef" v-model="inquiryForm.category_id" required>
                <option value="">Select Category</option> 
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <select class="form-select" ref="typeSelectRef" v-model="inquiryForm.type" required>
                <option value="">Select Type</option> 
                <option value="vegan">Vegan</option>
                <option value="eggless">Eggless</option>
                <option value="gluten-free">Gluten Free</option>                
              </select>
            </div>
            <div class="col-md-4">
              <select class="form-select" ref="productSelectRef" v-model="inquiryForm.product_id" required>
                <option value="">Select Flavour</option> 
                <option v-for="product in productsOptions" :key="product.id" :value="product.id">
                  {{ product.name }}
                </option>               
              </select>
            </div>
            <div class="col-12">
                <label class="visually-hidden" for="message_on_cake">Message On Cake</label>
                <input type="text" name="message_on_cake" id="message_on_cake" class="form-control" placeholder="Message On Cake" required v-model="inquiryForm.message_on_cake">
            </div>
            <div class="col-12">
                <label class="visually-hidden" for="special_instruction">Special Instruction</label>
                <input type="text" name="special_instruction" id="special_instruction" class="form-control" placeholder="Special Instruction" required v-model="inquiryForm.special_instruction">
            </div>
            <div class="col-12">
                <label class="visually-hidden" for="name">Full Name</label>
                <input type="text" name="name" id="name" class="form-control" placeholder="Name" required v-model="inquiryForm.name">
            </div>
            <div class="col-6">
                <label class="visually-hidden" for="phone">Phone</label>
                <input type="text" name="phone" class="form-control" id="phone" placeholder="Contact No" required v-model="inquiryForm.phone">
            </div>
            <div class="col-6">
                <label class="visually-hidden" for="email">Email</label>
                <input type="text" name="email" class="form-control" id="email" placeholder="Email" required v-model="inquiryForm.email">
            </div>                        
          </form>
        </div>

        <!-- Footer -->
        <div class="modal-footer">          
          <button
            type="submit"
            :disabled="inquiryLoading"
            class="button button-sm button-secondary button-zakaria"
            @click="submitInquiry"
          >
              {{ inquiryLoading ? "Sending..." : "Send Message" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { useCommonStore } from '@/stores/commonStore';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns'
import { toast } from 'vue3-toastify'
import { useLoaderStore } from '@/stores/loader';
import { api } from '@/plugins/axios';

const loader = useLoaderStore()
const commonStore = useCommonStore();
const { productsOptions, stores } = storeToRefs(commonStore)

const props = defineProps({
  customCake: {
    type: Object,
    required: true
  },
  categories: {
    type: Array,
    required: true,
    default: () => []
  }
})

const inquiryInitialForm = {
  custom_cake_id: null,
  pickup_date_time: null,
  category_id: '',
  type: '',
  product_id: '',
  message_on_cake: '',
  special_instruction: '',
  name: '',
  phone: '',
  email: '',
  store_id: '',
  delivery_option: 'pickup',
};
const inquiryForm = ref({ ...inquiryInitialForm });

const inquiryLoading = ref(false);
const categorySelectRef = ref(null)
const typeSelectRef = ref(null)
const productSelectRef = ref(null)

onMounted(() => {
  if (!stores.value.length) {
    commonStore.getStores()
  }

  const modalEl = document.getElementById('inquiryModal')
  if (modalEl && window.bootstrap) {
    new window.bootstrap.Modal(modalEl, {
      backdrop: 'static',
      keyboard: true
    })
  }

  window.$(categorySelectRef.value).on('change', (e) => {
    inquiryForm.value.category_id = e.target.value
    inquiryForm.value.product_id = ''
    commonStore.getProductsByCategory(e.target.value)    
  })

  window.$(typeSelectRef.value).on('change', (e) => {
    inquiryForm.value.type = e.target.value    
  })

  window.$(productSelectRef.value).on('change', (e) => {
    inquiryForm.value.product_id = e.target.value    
  })
  
})

watch(inquiryForm.value, (val) => {
  if (val.pickup_date_time) {
    inquiryForm.value.pickup_date_time = format(val.pickup_date_time, 'yyyy-MM-dd HH:mm:ss')    
  }
  inquiryForm.value.custom_cake_id = props.customCake.id
})

function submitInquiry() {
  inquiryLoading.value = true;
  loader.show('Sending inquiry...')
  api.post(`send-custome-cake-order-inquiry`, inquiryForm.value).then((res) => {    
    toast.success(res.data.message);

    // Reset form
    inquiryForm.value = { ...inquiryInitialForm };

    document.querySelector('#inquiryModal .btn-close').click();
  }).catch((err) => {
    if (err.response) {
      toast.error(err.response.data.message || "Submission failed.");
    } else {
      toast.error("Something went wrong. Please try again.");
    }    
  }).finally(() => {
    loader.hide()
    inquiryLoading.value = false;
  })
}

</script>

<style scoped>
.modal-content {
  font-family: 'Inter', sans-serif;
}
.list-group-item {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #f1f1f1;
}
.list-group-item:last-child {
  border-bottom: none;
}
.modal {
  z-index: 9999 !important;
}

.modal-dialog-scrollable .modal-body,
.modal-dialog-scrollable .modal-content{
  overflow: visible !important;
}
</style>
